import type { FocusEvent, KeyboardEvent } from 'react'

import { useCallback, useState } from 'react'
import { formatNumber } from '@/lib/format'

export type Scale = {
    min: number
    max: number
    step: number
    ticks: number[]
}

export type AxisFormat = 'compact' | 'number' | 'percent'

// Power of ten at or below the value. A loop instead of Math.log10 keeps the result
// identical in every JavaScript engine, so server and browser render the same ticks.
const magnitudeOf = (value: number): number => {
    let magnitude = 1

    if (value >= 1) {
        while (magnitude * 10 <= value) magnitude *= 10
    }
    else {
        while (magnitude > value && magnitude > 1e-9) magnitude /= 10
    }

    return magnitude
}

const niceStep = (span: number, count: number): number => {
    const rough = span / Math.max(1, count)
    const magnitude = magnitudeOf(rough)
    const residual = rough / magnitude
    const nice = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 2.5 ? 2.5 : residual <= 5 ? 5 : 10

    return nice * magnitude
}

// Rounded axis bounds and evenly spaced ticks
export const niceScale = (minValue: number, maxValue: number, count = 4, includeZero = false): Scale => {
    let low = includeZero ? Math.min(0, minValue) : minValue
    let high = includeZero ? Math.max(0, maxValue) : maxValue

    if (!Number.isFinite(low) || !Number.isFinite(high)) {
        low = 0
        high = 1
    }

    if (high === low) {
        high = low === 0 ? 1 : low + Math.abs(low) * 0.1
        if (!includeZero) low -= Math.abs(low) * 0.1
    }

    const step = niceStep(high - low, count)
    const min = Math.floor(low / step) * step
    const max = Math.ceil(high / step) * step
    const tickCount = Math.max(1, Math.round((max - min) / step))
    const ticks = Array.from({ length: tickCount + 1 }, (_, index) => min + index * step)

    return { min, max, step, ticks }
}

export const scaleRatio = (value: number, scale: Scale): number => (scale.max === scale.min ? 0 : (value - scale.min) / (scale.max - scale.min))

// Distance from the top of the plot, in percent
export const topPercent = (value: number, scale: Scale): number => (1 - scaleRatio(value, scale)) * 100

const decimalsOf = (value: number): number => {
    const text = String(Number(value.toPrecision(8)))

    if (text.includes('e-')) return Number(text.split('e-')[1])

    const dot = text.indexOf('.')
    return dot === -1 ? 0 : text.length - dot - 1
}

// Axis labels share one unit and one precision so they line up
export const formatAxisValue = (value: number, scale: Scale, format: AxisFormat): string => {
    if (format === 'percent') return `${formatNumber(value, Math.min(2, decimalsOf(scale.step)))}%`
    if (format === 'number') return formatNumber(value, Math.min(2, decimalsOf(scale.step)))

    const base = Math.max(Math.abs(scale.min), Math.abs(scale.max))
    const [unit, suffix]: [number, string] = base >= 1e9 ? [1e9, ' M'] : base >= 1e6 ? [1e6, ' jt'] : base >= 1e3 ? [1e3, ' rb'] : [1, '']

    return `${formatNumber(value / unit, Math.min(3, decimalsOf(scale.step / unit)))}${suffix}`
}

const sign = (value: number): number => (value < 0 ? -1 : value > 0 ? 1 : 0)

const fixed = (value: number): string => value.toFixed(2)

// Monotone cubic curve through the points (same tangents as d3 curveMonotoneX)
export const monotonePath = (points: [number, number][]): string => {
    const count = points.length

    if (count === 0) return ''
    if (count === 1) return `M${fixed(points[0][0])},${fixed(points[0][1])}`
    if (count === 2) return `M${fixed(points[0][0])},${fixed(points[0][1])}L${fixed(points[1][0])},${fixed(points[1][1])}`

    const tangents: number[] = new Array(count).fill(0)

    for (let index = 1; index < count - 1; index++) {
        const [x0, y0] = points[index - 1]
        const [x1, y1] = points[index]
        const [x2, y2] = points[index + 1]
        const h0 = x1 - x0
        const h1 = x2 - x1
        const s0 = h0 === 0 ? 0 : (y1 - y0) / h0
        const s1 = h1 === 0 ? 0 : (y2 - y1) / h1
        const p = h0 + h1 === 0 ? 0 : (s0 * h1 + s1 * h0) / (h0 + h1)

        tangents[index] = (sign(s0) + sign(s1)) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(p)) || 0
    }

    const firstSpan = points[1][0] - points[0][0]
    const lastSpan = points[count - 1][0] - points[count - 2][0]

    tangents[0] = firstSpan ? ((3 * (points[1][1] - points[0][1])) / firstSpan - tangents[1]) / 2 : tangents[1]
    tangents[count - 1] = lastSpan ? ((3 * (points[count - 1][1] - points[count - 2][1])) / lastSpan - tangents[count - 2]) / 2 : tangents[count - 2]

    let path = `M${fixed(points[0][0])},${fixed(points[0][1])}`

    for (let index = 0; index < count - 1; index++) {
        const [x0, y0] = points[index]
        const [x1, y1] = points[index + 1]
        const third = (x1 - x0) / 3

        path += `C${fixed(x0 + third)},${fixed(y0 + third * tangents[index])},${fixed(x1 - third)},${fixed(y1 - third * tangents[index + 1])},${fixed(x1)},${fixed(y1)}`
    }

    return path
}

// Which x labels to print so they never collide
export const pickLabelIndexes = (count: number, maxLabels = 6): number[] => {
    if (count <= 12) return Array.from({ length: count }, (_, index) => index)

    const step = Math.ceil((count - 1) / (maxLabels - 1))
    const indexes: number[] = []

    for (let index = 0; index < count; index += step) indexes.push(index)

    const lastPicked = indexes[indexes.length - 1]

    if (lastPicked !== count - 1) {
        if (count - 1 - lastPicked < step / 2) indexes.pop()
        indexes.push(count - 1)
    }

    return indexes
}

// Hover, touch and keyboard selection of one data point (crosshair or column)
export const useActiveIndex = (count: number) => {
    const [active, setActive] = useState<number | null>(null)

    const onKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
        if (count === 0) return

        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            event.preventDefault()
            setActive((current) => (current === null ? 0 : Math.min(count - 1, current + 1)))
        }
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            event.preventDefault()
            setActive((current) => (current === null ? count - 1 : Math.max(0, current - 1)))
        }
        else if (event.key === 'Home') {
            event.preventDefault()
            setActive(0)
        }
        else if (event.key === 'End') {
            event.preventDefault()
            setActive(count - 1)
        }
        else if (event.key === 'Escape') {
            setActive(null)
        }
    }, [count])

    const onFocus = useCallback((event: FocusEvent<HTMLElement>) => {
        if (event.target === event.currentTarget) setActive((current) => current ?? count - 1)
    }, [count])

    const onBlur = useCallback(() => setActive(null), [])

    return { active, setActive, onKeyDown, onFocus, onBlur }
}
